// frontend/src/hooks/useMlExperiment.ts

import { useState, useEffect, useCallback } from 'react';
import { MlExperimentService } from '../services/mlExperiment.service';
import { useAuth } from '../contexts/AuthContext';

export const useMlExperiment = () => {
  const { selectedTraceId, selectedDataset } = useAuth();

  const [experiments, setExperiments] = useState<any[]>([]);
  const [selectedExperiment, setSelectedExperiment] = useState<any | null>(null);
  const [analysis, setAnalysis] = useState<any | null>(null);
  const [summary, setSummary] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listExperiments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = selectedTraceId
        ? await MlExperimentService.getExperimentsByTraceId(selectedTraceId)
        : await MlExperimentService.listExperiments();
      setExperiments(data);
      
      // Se há um trace selecionado e a lista tem itens, seleciona o primeiro automaticamente
      if (selectedTraceId && data.length > 0 && !selectedExperiment) {
        setSelectedExperiment(data[0]);
        // Carrega o detalhe do primeiro experimento
        const detail = await MlExperimentService.analyzeExperiment(data[0].id);
        setAnalysis(detail);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erro ao listar experimentos');
    } finally {
      setLoading(false);
    }
  }, [selectedTraceId, selectedExperiment]);

  const loadSummary = useCallback(async (datasetName?: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await MlExperimentService.getSummary(
        datasetName ?? selectedDataset ?? undefined
      );
      setSummary(data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erro ao carregar resumo');
    } finally {
      setLoading(false);
    }
  }, [selectedDataset]);

  const loadExperiment = useCallback(async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await MlExperimentService.analyzeExperiment(id);
      setAnalysis(data);
      
      const experiment = experiments.find(e => e.id === id);
      if (experiment) {
        setSelectedExperiment(experiment);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erro ao carregar experimento');
    } finally {
      setLoading(false);
    }
  }, [experiments]);

  const selectExperiment = useCallback((experiment: any) => {
    setSelectedExperiment(experiment);
    loadExperiment(experiment.id);
  }, [loadExperiment]);

  useEffect(() => {
    listExperiments();
    loadSummary();
  }, [listExperiments, loadSummary]);

  return {
    experiments,
    selectedExperiment,
    analysis,
    summary,
    loading,
    error,
    listExperiments,
    loadSummary,
    loadExperiment,
    selectExperiment,
  };
};